import { memo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { ICluster, IContent, IContentItemClass } from 'src/interface';
import { isDesktopSelector } from 'src/selectors/app';
import {
    ALIGN_TYPE,
    Element,
    Image,
    LazyloadImage,
    RouterLink,
} from 'src/components';
import Card from 'src/components/card';
import { ContentFooter } from '../../shared/ContentFooter';
import { ContentTitle } from '../../shared/ContentTitle';
import { ContentDescription } from '../../shared/ContentDescription';
import { ContentCluster } from '../../shared/ContentCluster';

interface IProps {
    item: IContent;
    align?: ALIGN_TYPE;
    domRef?: any;
    showDescription?: boolean;
    showCluster?: boolean;
    displayTimeIn?: number;
    lazyloadImage?: boolean;
    ContentItemClass?: IContentItemClass;
    onClick: (evt: React.MouseEvent) => void;
}

export function ContentPhotoItem(props: IProps) {
    const {
        item,
        onClick,
        showDescription = false,
        showCluster = false,
        displayTimeIn,
        lazyloadImage,
        ContentItemClass = {},
    } = props;

    const {
        CardClass,
        CardContentClass,
        CardHeaderClass = '',
        CardImageClass = '',
        ClusterClass,
    } = ContentItemClass;

    const isDesktop = useSelector(isDesktopSelector);

    const renderImages = useCallback((item: IContent) => {
        if (!item.images || !item.images.length) {
            return null;
        }

        return item.images?.slice(0, 3)?.map((image, index) => {
            return (
                <Card.Image
                    key={index}
                    className={cn(
                        CardImageClass,
                        'rounded-[4px]',
                        'md:h-[150px]',
                    )}
                >
                    {lazyloadImage ? (
                        <LazyloadImage src={image.url} alt={item.title} />
                    ) : (
                        <Image src={image.url} alt={item.title} />
                    )}
                </Card.Image>
            );
        });
    }, []);

    return (
        <>
            <RouterLink
                onClick={onClick}
                className={cn(
                    'is-multi-photo',
                    'bm-card',
                    'flex-col',
                    CardClass,
                )}
                to={isDesktop ? item.redirectUrl : item.url}
            >
                <Card.Header className={cn(CardHeaderClass, 'md:hidden')}>
                    <ContentTitle title={item.title} />
                </Card.Header>
                <Element className="overflow-hidden">
                    <Element
                        className={cn(
                            'bm-multi-card-image',
                            'relative',
                            'flex justify-between',
                            'w-full',
                            'mt-[12px] mb-[8px] mx-0',
                            'rounded-[4px]',
                            'overflow-hidden overflow-x-auto',
                            '[scrollbar-width:none]',
                            '[&::-webkit-scrollbar]:hidden',
                            'before:absolute before:left-[26.5%] before:bottom-[4px]',
                            'before:inline-block',
                            'before:z-[2]',
                            'before:text-white',
                            'before:flex before:items-center before:justify-center',
                            'before:[line-height:1]',
                            'before:[text-shadow:0.5px_0.5px_1px_#00000033]',
                            'md:m-0 md:rounded-none',
                            'md:before:left-[200px]',
                            'item-photo before:text-[20px]'
                        )}
                    >
                        {renderImages(item)}
                    </Element>
                </Element>
                <Card.Content className={cn(CardContentClass, '!m-0 !pt-0')}>
                    <Card.Header
                        className={cn(CardHeaderClass, 'max-md:hidden')}
                    >
                        <ContentTitle title={item.title} />
                    </Card.Header>
                    <Card.Footer>
                        <ContentFooter
                            content={item}
                            displayTimeIn={displayTimeIn}
                            {...props}
                        />
                    </Card.Footer>
                    {showDescription && (
                        <ContentDescription
                            description={item.description}
                            objEllipsis={{ widthContainer: 212, line: 4 }}
                        />
                    )}
                </Card.Content>
            </RouterLink>
            {showCluster && (
                <ContentCluster
                    className={ClusterClass}
                    cluster={item.cluster as ICluster}
                />
            )}
        </>
    );
}

export default memo(ContentPhotoItem);
